import { CATALOGUE_VERSION } from "../data/catalogue";
import {
  MAX_ANSWER_ROWS_PER_SEAT,
  MAX_CUSTOM_ENTRIES,
} from "../../shared/protocol";
import type {
  AnswersImportPayload,
  CatalogueItem,
  Experience,
  ExportRecord,
  Framing,
  Interest,
  LocalSession,
  Participant,
  ParticipantAnswer,
  RemoteRoomView,
  Seat,
  SharedNote,
  StanceDefault,
} from "../types";
import { MAX_PERSONAL_CONTEXT_LENGTH } from "../types";
import {
  allItems,
  answerHasContent,
  answerIsComplete,
  mergePersonalText,
  normalizeParticipantAnswer,
  otherSeat,
} from "./session";

const LEGACY_OWN_ANSWERS_CSV_FORMAT = "feel-scene-answers-v1";
const OWN_ANSWERS_CSV_FORMAT = "feel-scene-answers-v2";
export const MAX_OWN_ANSWERS_CSV_BYTES = 5 * 1024 * 1024;
const MAX_IMPORT_ITEMS = MAX_ANSWER_ROWS_PER_SEAT;
const MAX_IMPORT_ROWS = MAX_IMPORT_ITEMS * 2;
const MAX_IMPORT_CELL_CHARACTERS = MAX_PERSONAL_CONTEXT_LENGTH + 1;
const FORMULA_PREFIX = /^[=+\-@\t\r\n＝＋－＠]/u;
const OWN_ANSWERS_HEADERS = [
  "feel_scene_format",
  "catalogue_version",
  "item_id",
  "term",
  "custom",
  "reference_definition",
  "framing",
  "stance_default",
  "perspective_index",
  "perspective",
  "enabled",
  "interest",
  "experience",
  "personal_meaning",
  "personal_context",
  "important",
] as const;
const OWN_ANSWERS_V2_HEADERS = OWN_ANSWERS_HEADERS.filter(
  (header) => header !== "personal_meaning",
);

export interface OwnAnswersImport extends AnswersImportPayload {
  catalogueVersion: number;
  answers: ParticipantAnswer[];
  customEntries: CatalogueItem[];
  replacementCount: number;
  skippedItemIds: string[];
}

export interface SessionExport {
  product: "Feel Scene";
  catalogueVersion: number;
  exportedAt: string;
  room: {
    mode: "local" | "remote";
    createdAt: string;
    expiresAt: string;
  };
  participants: Record<Seat, Participant>;
  viewerSeat: Seat;
  records: ExportRecord[];
}

export function buildSessionExport(
  session: LocalSession | RemoteRoomView,
): SessionExport {
  const viewerSeat = session.mode === "local" ? session.activeSeat : session.seat;
  const partnerSeat = otherSeat(viewerSeat);
  const items = allItems(session.customEntries);
  let ownAnswers: Record<string, ParticipantAnswer>;
  let partnerAnswers: Record<string, ParticipantAnswer>;
  let partnerSaved: Set<string>;
  let notes: Record<string, SharedNote>;

  if (session.mode === "local") {
    ownAnswers = session.answers[viewerSeat];
    partnerAnswers = session.answers[partnerSeat];
    partnerSaved = new Set(
      Object.values(partnerAnswers)
        .filter((answer) => answer.savedAt !== null)
        .map((answer) => answer.itemId),
    );
    notes = session.notes;
  } else {
    ownAnswers = session.ownAnswers;
    partnerAnswers = session.partnerAnswers;
    partnerSaved = new Set([
      ...session.partnerSaved,
      ...Object.keys(session.partnerAnswers),
    ]);
    notes = session.notes;
  }

  const records = items.map((item) => {
    const own = ownAnswers[item.id] ?? null;
    const partner = partnerAnswers[item.id] ?? null;
    const ownSaved = own?.savedAt !== null && own?.savedAt !== undefined;
    const hasPartnerSaved = partnerSaved.has(item.id);
    const mutuallySaved = ownSaved && hasPartnerSaved;
    const revealState: ExportRecord["revealState"] = mutuallySaved
      ? "revealed"
      : ownSaved || hasPartnerSaved
        ? "pending"
        : "unanswered";
    return {
      item,
      note: notes[item.id] ?? null,
      answers: {
        [viewerSeat]: own,
        [partnerSeat]:
          session.mode === "local"
            ? partner
            : mutuallySaved
              ? (partner ?? null)
              : hasPartnerSaved
                ? "redacted"
                : null,
      } as Record<Seat, ParticipantAnswer | null | "redacted">,
      revealState,
    };
  });

  return {
    product: "Feel Scene",
    catalogueVersion: session.catalogueVersion || CATALOGUE_VERSION,
    exportedAt: new Date().toISOString(),
    room: {
      mode: session.mode,
      createdAt: new Date(session.createdAt).toISOString(),
      expiresAt: new Date(session.expiresAt).toISOString(),
    },
    participants: session.participants,
    viewerSeat,
    records,
  };
}

export function buildOwnAnswersCsv(data: SessionExport) {
  const rows: string[][] = [[...OWN_ANSWERS_V2_HEADERS]];

  for (const record of data.records) {
    const answer = record.answers[data.viewerSeat];
    if (!answer || answer === "redacted" || !answerHasContent(answer)) continue;
    const labels = [
      ...record.item.perspectives,
      ...Object.keys(answer.perspectives).filter(
        (label) => !record.item.perspectives.includes(label),
      ),
    ];
    for (const [index, perspective] of labels.entries()) {
      const value = answer.perspectives[perspective];
      if (!value) continue;
      rows.push([
        OWN_ANSWERS_CSV_FORMAT,
        String(data.catalogueVersion),
        record.item.id,
        record.item.term,
        String(Boolean(record.item.custom)),
        record.item.definition,
        record.item.framing,
        record.item.stanceDefault,
        String(index + 1),
        perspective,
        String(value.enabled),
        value.interest ?? "",
        value.experience ?? "",
        answer.context,
        String(answer.important),
      ]);
    }
  }

  return `\uFEFF${rows
    .map((row) => row.map(csvCell).join(","))
    .join("\r\n")}`;
}

export function buildOwnAnswersCsvFile(data: SessionExport) {
  return {
    filename: `feel-scene-my-answers-${dateStamp()}.csv`,
    contents: buildOwnAnswersCsv(data),
    // Firefox may infer a generic Blob back to text/csv from the filename and
    // apply an external helper-app rule. Keep the alternate CSV type explicit
    // so its configured save/download handling remains in control.
    type: "application/csv",
  };
}

export function parseOwnAnswersCsv(
  contents: string,
  data: SessionExport,
): OwnAnswersImport {
  if (contents.length > MAX_OWN_ANSWERS_CSV_BYTES) {
    throw new Error("This CSV is larger than 5 MB. Choose a Feel Scene answers CSV.");
  }
  const table = parseCsv(contents.replace(/^\uFEFF/, "")).map((row) =>
    row.map(decodeCsvCell),
  );
  if (table.length < 2) {
    throw new Error("This CSV does not contain any Feel Scene answers.");
  }
  const headers = table[0];
  const legacyFormat = headersMatch(headers, OWN_ANSWERS_HEADERS);
  const currentFormat = headersMatch(headers, OWN_ANSWERS_V2_HEADERS);
  if (!legacyFormat && !currentFormat) {
    throw new Error("This is not a Feel Scene answers CSV.");
  }
  const activeHeaders = legacyFormat
    ? OWN_ANSWERS_HEADERS
    : OWN_ANSWERS_V2_HEADERS;
  if (table.slice(1).some((row) => row.length !== activeHeaders.length)) {
    throw new Error("This CSV is malformed. Choose an unedited Feel Scene answers CSV.");
  }
  const column = new Map(
    activeHeaders.map((header, index) => [header, index]),
  );
  const value = (row: string[], name: (typeof OWN_ANSWERS_HEADERS)[number]) =>
    column.has(name) ? row[column.get(name)!] : "";
  const rows = table.slice(1);
  if (!rows.length) {
    throw new Error("This CSV does not contain any Feel Scene answers.");
  }
  const expectedFormat = legacyFormat
    ? LEGACY_OWN_ANSWERS_CSV_FORMAT
    : OWN_ANSWERS_CSV_FORMAT;
  if (rows.some((row) => value(row, "feel_scene_format") !== expectedFormat)) {
    throw new Error("This CSV uses an unsupported Feel Scene answers format.");
  }
  const versions = new Set(rows.map((row) => value(row, "catalogue_version")));
  if (versions.size !== 1) {
    throw new Error("This CSV contains more than one catalogue version.");
  }
  const catalogueVersion = Number([...versions][0]);
  if (!Number.isInteger(catalogueVersion) || catalogueVersion < 1) {
    throw new Error("This CSV has an invalid catalogue version.");
  }
  if (catalogueVersion > data.catalogueVersion) {
    throw new Error(
      `This CSV uses catalogue v${catalogueVersion}, but this room uses v${data.catalogueVersion}. Update Feel Scene before importing it.`,
    );
  }

  const rowsByItem = new Map<string, string[][]>();
  for (const row of rows) {
    const itemId = value(row, "item_id");
    if (!validEntityId(itemId)) {
      throw new Error("This CSV contains an invalid item ID.");
    }
    const itemRows = rowsByItem.get(itemId) ?? [];
    itemRows.push(row);
    rowsByItem.set(itemId, itemRows);
  }
  if (rowsByItem.size > MAX_IMPORT_ITEMS) {
    throw new Error(`This CSV contains more than ${MAX_IMPORT_ITEMS} answers.`);
  }

  const currentById = new Map(
    data.records.map((record) => [record.item.id, record]),
  );
  const answers: ParticipantAnswer[] = [];
  const customEntries: CatalogueItem[] = [];
  const skippedItemIds: string[] = [];
  let replacementCount = 0;

  for (const [itemId, itemRows] of rowsByItem) {
    const custom = parseBoolean(
      consistentValue(itemRows, (row) => value(row, "custom"), itemId),
      "custom",
      itemId,
    );
    const existing = currentById.get(itemId);
    let item = existing?.item;
    if (!item && !custom) {
      skippedItemIds.push(itemId);
      continue;
    }
    if (!item) {
      item = customItemFromRows(itemId, itemRows, value, data.viewerSeat);
      customEntries.push(item);
    } else if (Boolean(item.custom) !== custom) {
      throw new Error(`Item ${itemId} conflicts with this room's catalogue.`);
    } else if (custom) {
      const importedItem = customItemFromRows(
        itemId,
        itemRows,
        value,
        data.viewerSeat,
      );
      if (
        item.term !== importedItem.term ||
        item.definition !== importedItem.definition ||
        item.framing !== importedItem.framing ||
        item.stanceDefault !== importedItem.stanceDefault ||
        item.perspectives.join("\u0000") !==
          importedItem.perspectives.join("\u0000")
      ) {
        throw new Error(`Custom item ${itemId} conflicts with this room.`);
      }
    }

    const meaning = consistentValue(itemRows, (row) => value(row, "personal_meaning"), itemId);
    const context = mergePersonalText(
      meaning,
      consistentValue(itemRows, (row) => value(row, "personal_context"), itemId),
    );
    const important = parseBoolean(
      consistentValue(itemRows, (row) => value(row, "important"), itemId),
      "important",
      itemId,
    );
    if (meaning.length > 2_000 || context.length > MAX_PERSONAL_CONTEXT_LENGTH) {
      throw new Error(`Item ${itemId} contains personal text that is too long.`);
    }

    const sourceFraming = consistentValue(
      itemRows,
      (row) => value(row, "framing"),
      itemId,
    );
    if (!(["shared", "directional", "role-paired"] as const).includes(
      sourceFraming as Framing,
    )) {
      throw new Error(`Item ${itemId} has invalid response framing.`);
    }
    const sourcePerspectiveCount = sourceFraming === "shared" ? 1 : 2;
    const indexedRows = itemRows
      .map((row) => ({
        row,
        index: Number(value(row, "perspective_index")),
        label: value(row, "perspective").trim(),
      }))
      .sort((left, right) => left.index - right.index);
    if (
      indexedRows.length !== sourcePerspectiveCount ||
      indexedRows.some(
        (entry, index) =>
          !Number.isInteger(entry.index) ||
          entry.index !== index + 1,
      )
    ) {
      throw new Error(`Item ${itemId} has an invalid perspective position.`);
    }
    if (
      indexedRows.some((entry) => !entry.label || entry.label.length > 80) ||
      new Set(indexedRows.map((entry) => entry.label)).size !== indexedRows.length
    ) {
      throw new Error(`Item ${itemId} has invalid perspective labels.`);
    }

    const sourcePerspectives = Object.create(
      null,
    ) as ParticipantAnswer["perspectives"];
    for (const { row, label } of indexedRows) {
      const interest = optionalChoice(
        value(row, "interest"),
        ["need", "yearn", "want", "open", "dislike", "hard-limit"] as const,
        "interest",
        itemId,
      );
      const experience = optionalChoice(
        value(row, "experience"),
        ["expert", "confident", "familiar", "learning", "once", "never"] as const,
        "experience",
        itemId,
      );
      sourcePerspectives[label] = {
        enabled: parseBoolean(value(row, "enabled"), "enabled", itemId),
        interest: interest as Interest | null,
        experience: experience as Experience | null,
      };
    }
    const current = existing?.answers[data.viewerSeat];
    const currentAnswer = current && current !== "redacted" ? current : null;
    if (currentAnswer) replacementCount += 1;
    const sourceAnswer: ParticipantAnswer = {
      itemId,
      perspectives: sourcePerspectives,
      responseScaleVersion: 2,
      meaning: "",
      context,
      important,
      savedAt: null,
      updatedAt: Date.now(),
      version: currentAnswer?.version ?? 0,
      changedAfterReveal: currentAnswer?.changedAfterReveal ?? false,
    };
    if (!answerIsComplete(sourceAnswer)) {
      throw new Error(`Item ${itemId} has an incomplete structured answer.`);
    }
    const answer = normalizeParticipantAnswer(
      sourceAnswer,
      item,
      data.participants[data.viewerSeat].stance,
    );
    answers.push(answer);
  }

  if (!answers.length) {
    throw new Error("None of the answers in this CSV match this catalogue.");
  }
  const existingCustomCount = data.records.filter(
    (record) => record.item.custom,
  ).length;
  if (existingCustomCount + customEntries.length > MAX_CUSTOM_ENTRIES) {
    throw new Error(
      `This import would exceed the ${MAX_CUSTOM_ENTRIES}-custom-term room limit.`,
    );
  }
  return {
    catalogueVersion,
    answers,
    customEntries,
    replacementCount,
    skippedItemIds,
  };
}

export function usefulRecords(data: SessionExport) {
  return data.records.filter(
    (record) =>
      record.revealState !== "unanswered" ||
      Boolean(record.note?.text.trim()) ||
      Boolean(record.note?.important) ||
      Object.values(record.answers).some(
        (answer) =>
          answer !== null &&
          answer !== "redacted" &&
          answer.context.trim() !== "",
      ),
  );
}

function csvCell(value: string) {
  const safe = FORMULA_PREFIX.test(value) || value.startsWith("\u200B")
    ? `\u200B${value}`
    : value;
  return `"${safe.replaceAll('"', '""')}"`;
}

function decodeCsvCell(value: string) {
  return value.startsWith("\u200B") ? value.slice(1) : value;
}

function headersMatch(
  headers: string[],
  expected: readonly string[],
) {
  return (
    headers.length === expected.length &&
    headers.every((header, index) => header === expected[index])
  );
}

function parseCsv(contents: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let state: "start" | "unquoted" | "quoted" | "after-quote" = "start";

  const malformed = () => {
    throw new Error("This CSV is malformed. Choose an unedited Feel Scene answers CSV.");
  };
  const append = (character: string) => {
    cell += character;
    if (cell.length > MAX_IMPORT_CELL_CHARACTERS) malformed();
  };
  const finishCell = () => {
    row.push(cell);
    if (row.length > OWN_ANSWERS_HEADERS.length) malformed();
    cell = "";
    state = "start";
  };
  const finishRow = () => {
    finishCell();
    if (
      row.length !== OWN_ANSWERS_HEADERS.length &&
      row.length !== OWN_ANSWERS_V2_HEADERS.length
    ) {
      malformed();
    }
    rows.push(row);
    if (rows.length > MAX_IMPORT_ROWS + 1) {
      throw new Error(`This CSV contains more than ${MAX_IMPORT_ITEMS} answers.`);
    }
    row = [];
  };

  for (let index = 0; index < contents.length; index += 1) {
    const character = contents[index];
    if (state === "quoted") {
      if (character === '"' && contents[index + 1] === '"') {
        append('"');
        index += 1;
      } else if (character === '"') {
        state = "after-quote";
      } else {
        append(character);
      }
      continue;
    }

    if (state === "after-quote") {
      if (character === ",") {
        finishCell();
      } else if (character === "\n") {
        finishRow();
      } else if (character === "\r" && contents[index + 1] === "\n") {
        finishRow();
        index += 1;
      } else {
        malformed();
      }
      continue;
    }

    if (character === '"') {
      if (state !== "start") malformed();
      state = "quoted";
    } else if (character === ",") {
      finishCell();
    } else if (character === "\n") {
      finishRow();
    } else if (character === "\r") {
      if (contents[index + 1] !== "\n") malformed();
      finishRow();
      index += 1;
    } else {
      state = "unquoted";
      append(character);
    }
  }
  if (state === "quoted") {
    throw new Error("This CSV has an unfinished quoted value.");
  }
  if (cell !== "" || row.length || state === "after-quote") finishRow();
  return rows;
}

function parseBoolean(value: string, field: string, itemId: string) {
  if (value === "true") return true;
  if (value === "false") return false;
  throw new Error(`Item ${itemId} has an invalid ${field} value.`);
}

function optionalChoice<T extends readonly string[]>(
  value: string,
  choices: T,
  field: string,
  itemId: string,
): T[number] | null {
  if (value === "") return null;
  if (choices.includes(value)) return value as T[number];
  throw new Error(`Item ${itemId} has an invalid ${field} value.`);
}

function consistentValue(
  rows: string[][],
  read: (row: string[]) => string,
  itemId: string,
) {
  const values = new Set(rows.map(read));
  if (values.size !== 1) {
    throw new Error(`Item ${itemId} repeats inconsistent answer text.`);
  }
  return [...values][0];
}

function customItemFromRows(
  itemId: string,
  rows: string[][],
  value: (
    row: string[],
    name: (typeof OWN_ANSWERS_HEADERS)[number],
  ) => string,
  seat: Seat,
): CatalogueItem {
  if (!itemId.startsWith("custom.")) {
    throw new Error(`Item ${itemId} is not a valid custom item.`);
  }
  const term = consistentValue(rows, (row) => value(row, "term"), itemId).trim();
  const definition = consistentValue(
    rows,
    (row) => value(row, "reference_definition"),
    itemId,
  ).trim();
  const framing = consistentValue(rows, (row) => value(row, "framing"), itemId);
  const stanceDefault = consistentValue(
    rows,
    (row) => value(row, "stance_default"),
    itemId,
  );
  if (!term || term.length > 100 || definition.length > 500) {
    throw new Error(`Custom item ${itemId} has invalid shared text.`);
  }
  if (!["shared", "directional", "role-paired"].includes(framing)) {
    throw new Error(`Custom item ${itemId} has invalid response framing.`);
  }
  if (!["both", "dominant-first", "dominant-second"].includes(stanceDefault)) {
    throw new Error(`Custom item ${itemId} has an invalid stance default.`);
  }
  const indexed = rows
    .map((row) => ({
      index: Number(value(row, "perspective_index")),
      label: value(row, "perspective").trim(),
    }))
    .sort((left, right) => left.index - right.index);
  if (
    indexed.some(
      (entry, index) =>
        entry.index !== index + 1 || !entry.label || entry.label.length > 80,
    ) ||
    new Set(indexed.map((entry) => entry.label)).size !== indexed.length
  ) {
    throw new Error(`Custom item ${itemId} has invalid perspectives.`);
  }
  const expectedPerspectiveCount = framing === "shared" ? 1 : 2;
  if (indexed.length !== expectedPerspectiveCount) {
    throw new Error(`Custom item ${itemId} has the wrong number of perspectives.`);
  }
  return {
    id: itemId,
    term,
    definition,
    aliases: [],
    framing: framing as Framing,
    stanceDefault: stanceDefault as StanceDefault,
    perspectives: indexed.map((entry) => entry.label),
    categoryId: "custom",
    categoryName: "Custom terms",
    subgroupId: null,
    subgroupName: null,
    custom: true,
    createdBy: seat,
    locked: false,
    customVersion: 0,
  };
}

function validEntityId(value: string) {
  return (
    value.length >= 2 &&
    value.length <= 128 &&
    /^[A-Za-z0-9._:-]+$/.test(value)
  );
}

function dateStamp() {
  return new Date().toISOString().slice(0, 10);
}
